import { useCallback, useEffect, useRef } from "react"

import type { Result as SearchProduct } from "@/generated/api/types.gen"
import { useScreenVisible } from "@/platform/app-mode/use-screen-visible"
import { buildGA4ItemFromSearchProduct } from "@/platform/observability/analytics/v2/mappers"
import { trackViewItemList } from "@/platform/observability/analytics/v2/search"
import { logger } from "@/platform/observability/logger"

interface BatchedViewTrackingReturn {
  trackProducts: (products: SearchProduct[], startIndex?: number) => void
}

export const useBatchedViewTracking = (listId: string, listName: string): BatchedViewTrackingReturn => {
  const visible = useScreenVisible()
  const trackedProductsRef = useRef<Set<string>>(new Set())

  const trackProducts = useCallback(
    (products: SearchProduct[], startIndex: number = 0) => {
      if (!visible) return
      const newProducts = products.filter((product) => !trackedProductsRef.current.has(product.productGroupUuid))

      if (newProducts.length === 0) {
        return
      }

      newProducts.forEach((product) => {
        trackedProductsRef.current.add(product.productGroupUuid)
      })

      try {
        const items = newProducts.map((product) => {
          const originalIndex = products.indexOf(product)
          return buildGA4ItemFromSearchProduct(product, {
            index: startIndex + originalIndex,
            item_list_id: listId,
            item_list_name: listName,
          })
        })

        trackViewItemList({ item_list_id: listId, item_list_name: listName, items })
      } catch (error) {
        logger.error("Failed to track view item list", { listId, listName, batchSize: newProducts.length }, error instanceof Error ? error : undefined)
      }
    },
    [listId, listName, visible]
  )

  useEffect(() => {
    const trackedProducts = trackedProductsRef.current
    return () => {
      trackedProducts.clear()
    }
  }, [listId, listName])

  return { trackProducts }
}
